import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import HelpMarkdown from './HelpMarkdown';
import { pickLang } from '../../utils/helpCentre';

function Screenshot({ shot, lang, onZoom }) {
  if (!shot?.url) return null;
  const caption = pickLang(shot.caption, lang);
  return (
    <figure className="my-1">
      <button type="button" onClick={() => onZoom(shot)} className="block w-full rounded-lg border border-gray-200 overflow-hidden bg-gray-50 cursor-zoom-in">
        <img src={shot.url} alt={caption} loading="lazy" className="w-full h-auto block" />
      </button>
      {caption && <figcaption className="mt-1 text-xs text-gray-500">{caption}</figcaption>}
    </figure>
  );
}

// One help article — body with its screenshots ({{shot:N}} places screenshot
// N inline; any not placed are shown after the text) and a link to the page
// it's about. Used by Browse (panel and Help page) and the super admin
// editor's preview.
export default function HelpArticleView({ article, lang, onBack, onNavigate }) {
  const { t } = useTranslation();
  const [zoomed, setZoomed] = useState(null);
  const shots = article.screenshots || [];
  const body = pickLang(article.body, lang);
  const placed = new Set([...body.matchAll(/\{\{shot:(\d+)\}\}/g)].map((m) => Number(m[1])));
  const unplaced = shots.map((s, i) => ({ s, n: i + 1 })).filter(({ n }) => !placed.has(n));

  return (
    <div className="space-y-4">
      {onBack && (
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800">
          <ArrowLeftIcon className="h-3.5 w-3.5" />
          {t('admin:help.backToGuides', 'All guides')}
        </button>
      )}
      <div>
        <h2 className="text-base font-semibold text-gray-900 leading-snug">{pickLang(article.title, lang)}</h2>
        {pickLang(article.summary, lang) && <p className="mt-1 text-sm text-gray-500">{pickLang(article.summary, lang)}</p>}
      </div>
      <HelpMarkdown
        text={body}
        onNavigate={onNavigate}
        renderShot={(n) => <Screenshot shot={shots[n - 1]} lang={lang} onZoom={setZoomed} />}
      />
      {unplaced.length > 0 && (
        <div className="space-y-3">
          {unplaced.map(({ s, n }) => <Screenshot key={n} shot={s} lang={lang} onZoom={setZoomed} />)}
        </div>
      )}
      {article.route && (
        <Link
          to={article.route}
          onClick={onNavigate}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
        >
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          {t('admin:help.takeMeThere', 'Take me there')}
        </Link>
      )}

      {zoomed && (
        <div className="fixed inset-0 z-[1100] bg-black/80 flex items-center justify-center p-4" onClick={() => setZoomed(null)}>
          <button type="button" className="absolute top-4 right-4 text-white" onClick={() => setZoomed(null)}>
            <XMarkIcon className="h-7 w-7" />
          </button>
          <img src={zoomed.url} alt={pickLang(zoomed.caption, lang)} className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
